import MobileApp from "./MobileApp";
import AppShell from "./AppShell";
import React from "react";
import ResetPassword from "./ResetPassword";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  NavLink,
  useLocation,
} from "react-router-dom";
import Home from "./Home";
import About from "./About";
import Login from "./Login";
import History from "./History";
import GetExtension from "./GetExtension";
import NewsletterForm from "./NewsletterForm";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import RequireAuth from "./auth/RequireAuth";
import logo from "./assets/logo.png";

// Legacy hidden path — kept as a back-door alias for the extension page.
// The new public route is /get-extension (requires login per your spec).
const HIDDEN_EXTENSION_PATH = "/extension/factornot-early-access-9f34c";

/* ============================================================
   Brand mark
   ============================================================ */
function Brand({ size = 22, logoSize = 34 }) {
  return (
    <Link to="/" className="brand" aria-label="FactOrNot. — home">
      <div
        className="brand-logo"
        style={{ width: logoSize, height: logoSize, backgroundImage: `url(${logo})` }}
      />
      <div className="brand-mark" style={{ fontSize: size }}>
      <span className="g">Fact</span>
      <span className="a">Or</span>
      <span className="r">Not</span>
      </div>
    </Link>
  );
}

/* ============================================================
   Navigation — adapts to logged-in state
   ============================================================ */
function Nav() {
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  if (pathname.startsWith("/app")) return null;

  return (
    <header className="nav">
      <div className="nav-inner">
        <Brand />
        <nav className="nav-links">
          <NavLink to="/" end className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
            Home
          </NavLink>
          <NavLink to="/about" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
            About
          </NavLink>
          {user && (
            <>
              <NavLink to="/history" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
                History
              </NavLink>
              <NavLink to="/get-extension" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
                Extension
              </NavLink>
              <NavLink to="/mobile-app" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}>
  Mobile app
</NavLink>
            </>
          )}
        </nav>
        <div className="nav-spacer" />
        {user ? (
          <>
            <span className="user-chip" title={user.email}>
              <span className="user-dot" />
              {user.email}
            </span>
            <button className="btn btn-ghost" type="button" onClick={signOut}>
              Sign out
            </button>
          </>
        ) : (
          <>
            <Link to="/login" className="btn btn-ghost">Log in</Link>
            <Link to="/login" className="btn btn-ink">
              Get started <span aria-hidden="true">→</span>
            </Link>
          </>
        )}
      </div>
    </header>
  );
}

/* ============================================================
   Footer
   ============================================================ */
export function Footer() {
  const { user } = useAuth();
  return (
    <footer>
      <div className="foot-inner foot-inner-newsletter">
        <div>
          <div className="foot-brand">
            <div
              className="brand-logo"
              style={{ width: 28, height: 28, backgroundImage: `url(${logo})` }}
            />
            <div className="brand-mark" style={{ fontSize: 18 }}>
              <span className="g">Fact</span>{" "}
              <span className="a">Or</span>{" "}
              <span className="r">Not</span>
            </div>
          </div>
          <p className="foot-tag">
            An automated fact-checker. Paste a claim, get a sourced verdict in seconds.
          </p>
        </div>
        <div className="foot-newsletter-col">
          <h4>Newsletter</h4>
          <p className="foot-newsletter-pitch">
            The week&rsquo;s most-checked claims, with verdicts. No spam, no take-shopping.
          </p>
          <NewsletterForm variant="footer" source="footer" />
        </div>
        <div className="foot-col">
          <h4>Product</h4>
          <Link to="/">Check a claim</Link>
          {user && <Link to="/history">My history</Link>}
          {user && <Link to="/get-extension">Get the extension</Link>}
        </div>
        <div className="foot-col">
          <h4>Company</h4>
          <Link to="/about">About</Link>
          <Link to="/about">Methodology</Link>
          <Link to="/about">Corrections</Link>
        </div>
      </div>
      <div className="foot-bottom">
        <span>&copy; 2026 FactOrNot</span>
        <span>Privacy &middot; Terms &middot; Press</span>
      </div>
    </footer>
  );
}

/* ============================================================
   Scroll to top on route change
   ============================================================ */
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/* ============================================================ */
export default function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <Nav />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Logged-in only */}
          <Route
            path="/history"
            element={
              <RequireAuth>
                <History />
              </RequireAuth>
            }
          />
          <Route
            path="/get-extension"
            element={
              <RequireAuth>
                <GetExtension />
              </RequireAuth>
            }
          />
        <Route path="/mobile-app" element={<RequireAuth><MobileApp /></RequireAuth>} />
          <Route path="/app" element={<RequireAuth><AppShell /></RequireAuth>} />


          {/* Legacy hidden URL — still works, also requires login now */}
          <Route
            path={HIDDEN_EXTENSION_PATH}
            element={
              <RequireAuth>
                <GetExtension />
              </RequireAuth>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
}
